import type { AxiosResponse } from "axios";
import { getLanguageHeaders } from "src/services/commonLanguageHeader";
import { ServicesUrlEndpoints } from "src/services/servicesEndpointConstant";
import ApiService from "src/util/api.service";

export type WarehouseResourceType =
  | "warehouse"
  | "area"
  | "lane"
  | "shelf"
  | "position";

export type WarehouseActiveState =
  | boolean
  | {
      id?: boolean;
      name?: string;
    };

export interface WarehouseResource {
  id: string | number;
  name?: string | null;
  code?: string | null;
  description?: string | null;
  active?: WarehouseActiveState | null;
  warehouseType?: LookupOption | null;
  warehouseTypeName?: string | null;
  warehouse?: LookupOption | null;
  area?: LookupOption | null;
  lane?: LookupOption | null;
  shelf?: LookupOption | null;
  [key: string]: unknown;
}

export interface LookupOption {
  id: string | number;
  name?: string;
  code?: string;
  [key: string]: unknown;
}

export interface WarehouseListParams {
  size?: number;
  page?: number;
  sort?: "ASC" | "DESC";
  sortBy?: string;
  idDistributor?: string | number;
}

export interface WarehouseLookupParams {
  idDistributor?: string | number;
}

export interface WarehouseListResponse {
  warehouses?: WarehouseResource[];
  content?: WarehouseResource[];
  items?: WarehouseResource[];
  data?: WarehouseResource[];
  totalElements?: number;
  totalItems?: number;
  total?: number;
  page?: number;
  size?: number;
}

export interface WarehouseApiResponse {
  response?: WarehouseListResponse | WarehouseResource[];
}

export interface WarehouseResourcePayload {
  id?: string | number;
  name?: string;
  code?: string;
  description?: string;
  active: boolean;
  idDistributor?: string | number;
  warehouseTypeId?: string | number;
  warehouseId?: string | number;
  areaId?: string | number;
  laneId?: string | number;
  shelfId?: string | number;
}

export interface WarehouseCountMetric {
  key: string;
  labelKey: string;
  value?: number;
}

export interface WarehouseCountSummary {
  warehouses?: number;
  areas?: number;
  lanes?: number;
  shelves?: number;
  positions?: number;
}

const resourceEndpoints: Record<WarehouseResourceType, string> = {
  warehouse: ServicesUrlEndpoints.WAREHOUSE.BASE,
  area: ServicesUrlEndpoints.WAREHOUSE_AREA.BASE,
  lane: ServicesUrlEndpoints.WAREHOUSE_LANE.BASE,
  shelf: ServicesUrlEndpoints.WAREHOUSE_SHELF.BASE,
  position: ServicesUrlEndpoints.WAREHOUSE_POSITION.BASE,
};

export const getAllWarehousesByDistributor = (
  params: WarehouseListParams,
  language?: string,
): Promise<AxiosResponse<WarehouseApiResponse>> =>
  ApiService.get<WarehouseApiResponse>(
    ServicesUrlEndpoints.WAREHOUSE.LIST_BY_DISTRIBUTOR,
    {
      params,
      ...getLanguageHeaders(language),
    },
  );

export const getWarehouseLookup = (
  params: WarehouseLookupParams,
  language?: string,
): Promise<AxiosResponse<{ response?: LookupOption[] } | LookupOption[]>> =>
  ApiService.get(ServicesUrlEndpoints.WAREHOUSE.LOOKUP_BY_DISTRIBUTOR, {
    params,
    ...getLanguageHeaders(language),
  });

export const getWarehouseChildren = (
  resource: Exclude<WarehouseResourceType, "warehouse">,
  parentId: string | number,
  language?: string,
): Promise<AxiosResponse<WarehouseApiResponse>> => {
  const endpointByResource = {
    area: ServicesUrlEndpoints.WAREHOUSE_AREA.LIST_BY_WAREHOUSE.replace(
      "{warehouseId}",
      String(parentId),
    ),
    lane: ServicesUrlEndpoints.WAREHOUSE_LANE.LIST_BY_AREA.replace(
      "{areaId}",
      String(parentId),
    ),
    shelf: ServicesUrlEndpoints.WAREHOUSE_SHELF.LIST_BY_LANE.replace(
      "{laneId}",
      String(parentId),
    ),
    position: ServicesUrlEndpoints.WAREHOUSE_POSITION.LIST_BY_SHELF.replace(
      "{shelfId}",
      String(parentId),
    ),
  };

  return ApiService.get<WarehouseApiResponse>(
    endpointByResource[resource],
    getLanguageHeaders(language),
  );
};

export const getWarehouseChildLookup = (
  resource: Exclude<WarehouseResourceType, "warehouse">,
  parentId: string | number,
  language?: string,
): Promise<AxiosResponse<{ response?: LookupOption[] } | LookupOption[]>> => {
  const endpointByResource = {
    area: ServicesUrlEndpoints.WAREHOUSE_AREA.LOOKUP_BY_WAREHOUSE.replace(
      "{warehouseId}",
      String(parentId),
    ),
    lane: ServicesUrlEndpoints.WAREHOUSE_LANE.LOOKUP_BY_AREA.replace(
      "{areaId}",
      String(parentId),
    ),
    shelf: ServicesUrlEndpoints.WAREHOUSE_SHELF.LOOKUP_BY_LANE.replace(
      "{laneId}",
      String(parentId),
    ),
    position: ServicesUrlEndpoints.WAREHOUSE_POSITION.LOOKUP_BY_SHELF.replace(
      "{shelfId}",
      String(parentId),
    ),
  };

  return ApiService.get(endpointByResource[resource], {
    ...getLanguageHeaders(language),
  });
};

export const getWarehouseTypeLookup = (
  language?: string,
): Promise<AxiosResponse<{ response?: LookupOption[] } | LookupOption[]>> =>
  ApiService.get(ServicesUrlEndpoints.WAREHOUSE.TYPE_LOOKUP, {
    ...getLanguageHeaders(language),
  });

export const createWarehouseResource = (
  resource: WarehouseResourceType,
  payload: WarehouseResourcePayload,
  language?: string,
) =>
  ApiService.post(resourceEndpoints[resource], payload, {
    ...getLanguageHeaders(language),
  });

export const updateWarehouseResource = (
  resource: WarehouseResourceType,
  payload: WarehouseResourcePayload,
  language?: string,
) => {
  if (payload.id === undefined) {
    throw new Error(`Cannot update ${resource} without an id`);
  }

  return ApiService.patch(
    `${resourceEndpoints[resource]}/${payload.id}`,
    payload,
    {
      ...getLanguageHeaders(language),
    },
  );
};

export const deleteWarehouseResource = (
  resource: WarehouseResourceType,
  id: string | number,
  language?: string,
) =>
  ApiService.delete(`${resourceEndpoints[resource]}/${id}`, {
    ...getLanguageHeaders(language),
  });
